/**
 * KnowledgeWebSearchDetail — vibe-designer 1.185.0 Phase 7.2.
 *
 * "Web Search" detail panel — a 7th surface in the Knowledge tab that is
 * NOT a corpus. SearXNG-backed live query; no count, no ingest, no pillar
 * filter applicability.
 *
 * Availability sourced from tenantStore.mcpCapabilities.web_search (set
 * by /api/me probe). Backend gracefully degrades to empty results on
 * SearXNG-down — pair with the capability boolean for honest UX.
 */
import { useState, type FormEvent } from 'react';
import clsx from 'clsx';
import { Clock, History, Loader2, X } from 'lucide-react';
import { useKnowledgeStore } from '@/stores/knowledgeStore';
import { useTenantStore } from '@/stores/tenantStore';
import { useWebSearchHistory } from '@/hooks/useWebSearchHistory';

export function KnowledgeWebSearchDetail() {
  const webSearchResults = useKnowledgeStore((s) => s.webSearchResults);
  const isWebSearching = useKnowledgeStore((s) => s.isWebSearching);
  const webSearchError = useKnowledgeStore((s) => s.webSearchError);
  const runWebSearch = useKnowledgeStore((s) => s.runWebSearch);
  const mcpCapabilities = useTenantStore((s) => s.mcpCapabilities);
  const tenantId = useTenantStore((s) => s.tenantId);
  const available = mcpCapabilities?.web_search ?? false;

  // 1.189.0 Phase 7.5 — per-tenant query history (MRU, cap 10, dedup).
  const { history, append: appendHistory, clear: clearHistory } = useWebSearchHistory(tenantId);
  const [historyOpen, setHistoryOpen] = useState(false);

  const [draft, setDraft] = useState('');

  const submitQuery = (q: string) => {
    const trimmed = q.trim();
    if (!trimmed) return;
    appendHistory(trimmed);
    setHistoryOpen(false);
    void runWebSearch(trimmed);
  };

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    submitQuery(draft);
  };

  const onHistoryClick = (q: string) => {
    setDraft(q);
    submitQuery(q);
  };

  return (
    <div className="flex-1 overflow-y-auto p-6" data-testid="knowledge-web-search-detail">
      <header className="mb-4 flex items-center justify-between">
        <h1 className="text-lg font-semibold text-vibe-text">Web Search</h1>
        <span
          data-testid="knowledge-web-search-badge"
          className={clsx(
            'px-2 py-0.5 text-xs rounded',
            available
              ? 'bg-vibe-success/20 text-vibe-success'
              : 'bg-vibe-text-secondary/20 text-vibe-text-secondary',
          )}
        >
          {available ? 'Available' : 'Unavailable'}
        </span>
      </header>

      <div
        data-testid="knowledge-web-search-banner"
        className="rounded border border-vibe-border bg-vibe-bg-secondary p-3 text-sm text-vibe-text-secondary mb-4"
      >
        Not a corpus — query-time only; nothing to ingest. Searches the live
        web via SearXNG.
      </div>

      <form onSubmit={onSubmit} className="flex gap-2 mb-4 relative">
        <input
          data-testid="knowledge-web-search-input"
          type="text"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder="Search the web…"
          className="flex-1 px-3 py-1.5 text-sm rounded bg-vibe-bg-secondary border border-vibe-border text-vibe-text placeholder:text-vibe-text-secondary"
          disabled={isWebSearching || !available}
        />
        {/* 1.189.0 Phase 7.5 — recent-queries dropdown trigger.
            Disabled when history is empty so we don't open a vacuous dropdown. */}
        <button
          data-testid="knowledge-web-search-history-toggle"
          type="button"
          onClick={() => setHistoryOpen((v) => !v)}
          disabled={history.length === 0 || isWebSearching || !available}
          title={
            history.length === 0
              ? 'No recent searches yet'
              : `${historyOpen ? 'Hide' : 'Show'} recent searches (${history.length})`
          }
          aria-pressed={historyOpen}
          className="px-2 py-1.5 text-sm rounded border border-vibe-border bg-vibe-bg-secondary text-vibe-text-secondary hover:text-vibe-text disabled:opacity-40"
        >
          <History className="w-4 h-4" />
        </button>
        <button
          data-testid="knowledge-web-search-submit"
          type="submit"
          disabled={isWebSearching || !available || draft.trim().length === 0}
          className="px-3 py-1.5 text-sm rounded bg-vibe-accent text-vibe-bg disabled:opacity-50"
        >
          {isWebSearching ? 'Searching…' : 'Search'}
        </button>

        {historyOpen && history.length > 0 && (
          <div
            data-testid="knowledge-web-search-history-dropdown"
            className="absolute z-10 left-0 right-0 top-full mt-1 rounded border border-vibe-border bg-vibe-bg-secondary shadow-lg"
          >
            <div className="flex items-center justify-between px-3 py-1.5 border-b border-vibe-border text-xs text-vibe-text-secondary">
              <span className="flex items-center gap-1">
                <Clock className="w-3 h-3" />
                Recent searches
              </span>
              <button
                type="button"
                onClick={() => {
                  clearHistory();
                  setHistoryOpen(false);
                }}
                data-testid="knowledge-web-search-history-clear"
                className="flex items-center gap-1 hover:text-vibe-text"
                title="Clear all recent searches for this tenant"
              >
                <X className="w-3 h-3" />
                Clear
              </button>
            </div>
            <ul className="max-h-48 overflow-y-auto">
              {history.map((q, idx) => (
                <li key={`${q}-${idx}`}>
                  <button
                    type="button"
                    onClick={() => onHistoryClick(q)}
                    data-testid={`knowledge-web-search-history-item-${idx}`}
                    className="w-full text-left px-3 py-1.5 text-sm text-vibe-text hover:bg-vibe-bg-tertiary truncate"
                    title={q}
                  >
                    {q}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}
      </form>

      {isWebSearching && (
        <div
          data-testid="knowledge-web-search-loading"
          className="flex items-center gap-2 text-sm text-vibe-text-secondary py-4"
        >
          <Loader2 className="w-4 h-4 animate-spin" />
          <span>Searching SearXNG…</span>
        </div>
      )}

      {webSearchError && !isWebSearching && (
        <div
          data-testid="knowledge-web-search-error"
          className="rounded border border-vibe-warning/30 bg-vibe-warning/10 p-3 text-sm text-vibe-warning mb-4"
        >
          {webSearchError.message}
        </div>
      )}

      {!isWebSearching && webSearchResults && webSearchResults.results.length === 0 && (
        <div
          data-testid="knowledge-web-search-empty"
          className="text-sm text-vibe-text-secondary py-4"
        >
          No results for &ldquo;{webSearchResults.query}&rdquo;. SearXNG may be
          unavailable or the query produced no hits — check the Available badge
          above.
        </div>
      )}

      {!isWebSearching && webSearchResults && webSearchResults.results.length > 0 && (
        <ul className="space-y-3">
          {webSearchResults.results.map((r, idx) => (
            <li
              key={`${r.url}-${idx}`}
              data-testid={`knowledge-web-search-result-${idx}`}
              className="rounded border border-vibe-border bg-vibe-bg-secondary p-3"
            >
              <a
                href={r.url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm font-medium text-vibe-accent hover:underline"
              >
                {r.title}
              </a>
              <div className="text-xs text-vibe-text-secondary mt-0.5 break-all">{r.url}</div>
              {r.snippet && (
                <p className="text-sm text-vibe-text mt-2">{r.snippet}</p>
              )}
              {r.engine && (
                <span className="inline-block mt-2 px-1.5 py-0.5 text-[10px] uppercase tracking-wide rounded bg-vibe-bg-tertiary text-vibe-text-secondary">
                  {r.engine}
                </span>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
